import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import { darkTheme, lightTheme } from './Theme';

const STORAGE_KEY = 'portfolio-theme-mode';

const ThemeContext = createContext();

export function useThemeMode() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within ThemeContextProvider');
  }
  return context;
}

export function ThemeContextProvider({ children }) {
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved !== null) return saved === 'dark';
    // Default to dark, or respect system preference
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? true;
  });

  const theme = isDark ? darkTheme : lightTheme;

  const toggleTheme = useCallback(() => {
    setIsDark((prev) => !prev);
  }, []);

  // Persist to localStorage and set CSS variables for non-styled-component elements
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, isDark ? 'dark' : 'light');

    const root = document.documentElement;
    root.setAttribute('data-theme', isDark ? 'dark' : 'light');

    // Set CSS variables for elements outside styled-components (body, scrollbar, timeline CSS)
    root.style.setProperty('--bg-primary', theme.colors.bg_primary);
    root.style.setProperty('--bg-secondary', theme.colors.bg_secondary);
    root.style.setProperty('--bg-card', theme.colors.bg_card);
    root.style.setProperty('--bg-card-hover', theme.colors.bg_card_hover);
    root.style.setProperty('--heading', theme.colors.heading);
    root.style.setProperty('--para', theme.colors.para);
    root.style.setProperty('--muted', theme.colors.muted);
    root.style.setProperty('--accent', theme.colors.accent);
    root.style.setProperty('--border', theme.colors.border);
    root.style.setProperty('--border-light', theme.colors.border_light);
    root.style.setProperty('--shadow', theme.colors.shadow);
    root.style.setProperty('--overlay', theme.colors.overlay);
    root.style.setProperty('--glass-border', theme.colors.glass_border);
    root.style.setProperty('--glass-border-hover', theme.colors.glass_border_hover);
  }, [isDark, theme]);

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      <StyledThemeProvider theme={theme}>{children}</StyledThemeProvider>
    </ThemeContext.Provider>
  );
}
